import { AlertDialogService } from '@/app/services/alert-dialog.service';
import { AuthService } from '@/app/services/auth.service';
import { Component, computed, DestroyRef, effect, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AlertButtons, AlertComponent } from '../alert/alert.component';
import { UserActivityService } from '@/app/services/user-activity-service';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { fromEvent, map, startWith } from 'rxjs';
import { UserService } from '@/app/services/user.service';
dayjs.extend(relativeTime);

@Component({
  selector: 'cp-session-warning',
  imports: [],
  template: '',
})
export class SessionWarningComponent {

  private readonly userActivityService = inject(UserActivityService);
  private readonly alertDialogService = inject(AlertDialogService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly userService = inject(UserService);

  // Times in ms
  public ONE_MINUTE = 60000;
  public THREE_MINUTES = 180000;

  public FIFTEEN_SECONDS = 15000;
  // Display the session dialog when there is only 5 minutes left on the session
  public DIALOG_DISPLAY_THRESHOLD = 5;
  // The initial value is set to ensure that the dialog is not displayed
  public INITIAL_SESSION_VALUE = this.DIALOG_DISPLAY_THRESHOLD + 1;
  // The reference will be updated periodically via timers/intervals and will reflect the
  // number of minutes left on the session until it expires - after it will
  // have the value of 0 until it is reset
  public minutesLeft = signal<number>(this.INITIAL_SESSION_VALUE);
  public interval = signal<number>(this.ONE_MINUTE);
  public intervalHidden = signal<number>(this.THREE_MINUTES);
  private visibility = signal<'visible' | 'hidden'>(document.visibilityState);
  private dialogShown = signal<boolean>(false);
  private sessionExpired = signal<boolean>(false);

  constructor() {
    fromEvent(document, 'visibilitychange')
      .pipe(
        map(() => document.visibilityState),
        startWith(document.visibilityState)
      )
      .subscribe(value => this.visibility.set(value));


    effect(() => {
      if (this.visibility() === 'visible' && !this.sessionExpired() && this.userService.isAuthenticated()) {
        this.updateMinutesToSessionExpiryIfVisible();
      }
      // If GD tab is hidden
      if (this.isGDHidden()) {
        this.updateMinutesToSessionExpiryIfVisible();
      }
    });

    const intervalIdVisible = setInterval(() => {
      if (this.visibility() === 'visible' && !this.sessionExpired() && this.userService.isAuthenticated()) {
        this.updateMinutesToSessionExpiryIfVisible();
      }
    }, this.interval());

    this.destroyRef.onDestroy(() => clearInterval(intervalIdVisible));

    // If GD tab is hidden
    const intervalIdHidden = setInterval(() => {
      if (this.isGDHidden()) {
        this.updateMinutesToSessionExpiryIfVisible();
      }
    }, this.intervalHidden());

    this.destroyRef.onDestroy(() => clearInterval(intervalIdHidden));

    let previousValue = this.minutesLeft();
    
    effect(() => {
      const newValue = this.minutesLeft();
      const oldValue = previousValue;
      if (newValue !== oldValue) {
        // This will trigger the reset session when the user is active in Payroll tab
        // This will not trigger when the user is in GD Tab
        if(newValue === 15 && newValue > oldValue && this.isGDHidden()){
          this.resetSession();
        }
        const sessionIsExpired = newValue === 0;
        if (sessionIsExpired) {
          this.interval.set(this.FIFTEEN_SECONDS);
          this.intervalHidden.set(this.FIFTEEN_SECONDS);
          if (this.dialogShown()) {
            this.alertDialogService.close();
            this.dialogShown.set(false);
          }
          this.router.navigate(['/sessionexpired']);
        } else {
          const sessionWasExpired = oldValue === 0;

          if (newValue > this.DIALOG_DISPLAY_THRESHOLD) {
            this.interval.set((newValue - this.DIALOG_DISPLAY_THRESHOLD) * this.ONE_MINUTE);
            this.intervalHidden.set(((newValue - this.DIALOG_DISPLAY_THRESHOLD) * this.THREE_MINUTES))
          } else {
            this.interval.set(this.FIFTEEN_SECONDS);
            this.intervalHidden.set(this.FIFTEEN_SECONDS)
          }

          if (sessionWasExpired) {
            this.router.navigate(['../']);
          }
        }
      }
      previousValue = newValue;
    });

    effect(() => {
      if (this.showDialog() && !this.dialogShown()) {
        this.dialogShown.set(true);
        queueMicrotask(() => {
          this.closeDialog();
          this.displayDialog();
        });
      }
      //If the dialog is already shown but the user triggers the Validate API in payroll
      if(this.dialogShown() && !this.showDialog()){
        this.closeDialog();
        this.resetSession();
      }
      if(this.toRefresh()){ // Will trigger refresh on the last 7 minutes
        if(this.userActivityService.active() || this.visibility() === 'visible'){
          this.resetSession();
        }
      }
    });
  }

  private description = computed(() => {
    const time = this.minutesLeft() === 1 ? 'in 1 minute' : `${dayjs()
    .add(this.minutesLeft(), 'minutes')
    .fromNow()}`

    return `Your session will expire ${time} due to inactivity. Select 'Stay' to continue working.`;
    }
  )

  displayDialog() {
    const dialogData = {
      header: 'Warning: Session Expiring Soon',
      description: this.description,
      isXIconVisible: false,
      buttons: [
        {
          id: 'btn-alert-logout',
          text: 'Log Out',
          type: 'cancel',
          action: this.logoutHandler.bind(this)
        },
        {
          id: 'btn-alert-stay-in',
          text: 'Stay',
          type: 'action',
          action: this.stayInHandler.bind(this)
        }
      ] satisfies AlertButtons[]
    }

    setTimeout(() => {
      this.alertDialogService.open(
        dialogData.header,
        dialogData.description,
        dialogData.buttons, 
        dialogData.isXIconVisible,
        AlertComponent
      )
    }, 0);

    this.dialogShown.set(true);
  }

  closeDialog() {
    this.alertDialogService.close();
    this.dialogShown.set(false);
  }

  resetSession() {
    this.authService.validateUserSession(true);
    this.minutesLeft.set(this.INITIAL_SESSION_VALUE);
    this.userActivityService.initialize();
  }

  stayInHandler() {
    this.resetSession();
    this.closeDialog();
  }

  logoutHandler() {
    this.minutesLeft.set(this.INITIAL_SESSION_VALUE);
    this.router.navigate(['/logout'])
  }

  private async updateMinutesToSessionExpiryIfVisible() {
    try {
      const minutesToExpiry = await this.authService.testSession();
      this.minutesLeft.set(minutesToExpiry);
    } catch (error: unknown) {
      if ((error as { status?: number }).status === 401) {
        this.minutesLeft.set(0);
        this.sessionExpired.set(true);
      }
    }
  }

  public showDialog = computed(() => {
    return this.minutesLeft() !== 0 &&
      this.minutesLeft() <= this.DIALOG_DISPLAY_THRESHOLD
  });

  public toRefresh = computed(() => {
    return this.minutesLeft() !== 0 &&
      this.minutesLeft() === this.INITIAL_SESSION_VALUE + 1
  });

  private isGDHidden() {
    return this.visibility() === 'hidden' &&
    !this.sessionExpired() &&
      this.userService.isAuthenticated();
  }
}
